import useOrganizationPlan from "@/hooks/useOrganizationPlan";
import { FeatureKey } from "@/types/plans";
import {
    Box,
    ButtonBase,
    CircularProgress,
    Tooltip,
    Typography,
} from "@mui/material";
import { useMemo } from "react";

interface Props {
    feature: FeatureKey;
    usedQuota?: number;
}

const AvailableQuota = ({ feature, usedQuota }: Props) => {
    const { getFeatureQuota, openUpgradePopUp } = useOrganizationPlan(feature);

    const safeUsedQuota = typeof usedQuota === "number" ? usedQuota : 0;

    const totalQuota = useMemo(() => {
        return getFeatureQuota();
    }, [getFeatureQuota]);

    const percentage = totalQuota
        ? Math.min(100, Math.max(0, (safeUsedQuota / totalQuota) * 100))
        : 0;

    const isExceeded =
        totalQuota !== null && totalQuota !== undefined
            ? safeUsedQuota >= totalQuota
            : false;

    const handleClick = () => {
        if (totalQuota && safeUsedQuota >= totalQuota) {
            openUpgradePopUp();
        } else if (totalQuota) {
            openUpgradePopUp(`Has usado el ${Math.round(percentage)}% de tu cuota`);
        } else {
            openUpgradePopUp();
        }
    };

    const tooltipText = isExceeded
        ? totalQuota !== null
            ? `Cuota agotada (${safeUsedQuota} / ${totalQuota})`
            : "Cuota agotada"
        : totalQuota !== null
            ? `Disponible ${safeUsedQuota} / ${totalQuota}`
            : `${safeUsedQuota} usados`;

    return (
        <Tooltip title={tooltipText} placement="bottom" arrow>
            <ButtonBase
                onClick={handleClick}
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    alignSelf: "center",
                    transition:
                        "transform 0.15s ease-in-out, background-color 0.15s ease-in-out",
                    "&:hover": {
                        backgroundColor: "action.hover",
                    },
                }}
                aria-label={tooltipText}
            >
                <Box
                    sx={{
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                    }}
                >
                    {/* Background circle track */}
                    <CircularProgress
                        variant="determinate"
                        value={100}
                        size={40}
                        thickness={3.5}
                        sx={{
                            color: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "rgba(255, 255, 255, 0.12)"
                                    : "rgba(0, 0, 0, 0.08)",
                            position: "absolute",
                            left: 0,
                            top: 0,
                        }}
                    />

                    {/* Foreground progress */}
                    <CircularProgress
                        variant="determinate"
                        value={percentage}
                        size={40}
                        thickness={4}
                        color={
                            isExceeded ? "error" : percentage >= 75 ? "warning" : "primary"
                        }
                        sx={{
                            "& .MuiCircularProgress-circle": {
                                strokeLinecap: "round",
                            },
                        }}
                    />

                    {/* Center percentage label */}
                    <Box
                        sx={{
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: "0.68rem",
                                lineHeight: 1,
                                color: isExceeded ? "error.main" : "text.primary",
                            }}
                        >
                            {`${Math.round(percentage)}%`}
                        </Typography>
                    </Box>
                </Box>
            </ButtonBase>
        </Tooltip>
    );
};

export default AvailableQuota;
